import { useMemo } from 'react';
import type { AnalysisRun } from '@visiward/schemas';
import { PROMPT_CATEGORY_LABELS, type PromptCategory } from '@visiward/shared';
import {
  Gauge,
  MessageSquare,
  ThumbsUp,
  PieChart,
  TrendingUp,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge, EmptyState } from '@/components/ui/misc';
import { MetricCard } from './MetricCard';
import { CategoryBarChart, HorizontalBarChart, GaugeChart } from './charts';
import { RecommendationCard } from './RecommendationCard';
import { PromptResultsTable } from './PromptResultsTable';
import { CHART_COLORS } from './theme';
import { formatCurrency, formatNumber } from '@/lib/utils';

export function Dashboard({ run }: { run: AnalysisRun }) {
  const metrics = run.metrics;

  const categoryData = useMemo(() => {
    const map = new Map<PromptCategory, { total: number; mentioned: number }>();
    for (const r of run.promptResults) {
      const entry = map.get(r.category) ?? { total: 0, mentioned: 0 };
      entry.total += 1;
      if (r.brandMentioned) entry.mentioned += 1;
      map.set(r.category, entry);
    }
    return Array.from(map.entries()).map(([cat, v]) => ({
      name: PROMPT_CATEGORY_LABELS[cat].replace(' ', '\n'),
      value: v.total === 0 ? 0 : Math.round((v.mentioned / v.total) * 100),
    }));
  }, [run.promptResults]);

  const rankingData = useMemo(() => {
    const buckets = { 'Rank 1': 0, 'Rank 2–3': 0, 'Rank 4–5': 0, 'Unranked': 0, 'Not shown': 0 };
    for (const r of run.promptResults) {
      if (!r.brandMentioned) buckets['Not shown'] += 1;
      else if (r.rankPosition == null) buckets['Unranked'] += 1;
      else if (r.rankPosition === 1) buckets['Rank 1'] += 1;
      else if (r.rankPosition <= 3) buckets['Rank 2–3'] += 1;
      else if (r.rankPosition <= 5) buckets['Rank 4–5'] += 1;
      else buckets['Unranked'] += 1;
    }
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [run.promptResults]);

  const competitorData = useMemo(
    () =>
      (metrics?.competitorShareOfVoice ?? [])
        .slice(0, 6)
        .map((c) => ({ name: c.name, value: c.share })),
    [metrics],
  );

  // Potential Impact: an estimate, clearly not actual revenue.
  const potentialImpact = useMemo(() => {
    if (!metrics) return 0;
    const gap = 100 - metrics.visibilityScore;
    return Math.round(gap * 1250 + metrics.totalMentions * 800);
  }, [metrics]);

  if (!metrics) {
    return (
      <EmptyState
        title="Analysis could not produce metrics"
        description="The analysis did not return enough data. Please try running it again."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Top metric cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard
          label="AI Visibility Score"
          value={metrics.visibilityScore.toFixed(1)}
          suffix="/100"
          icon={Gauge}
          accent="blue"
        />
        <MetricCard
          label="Total Mentions"
          value={formatNumber(metrics.totalMentions)}
          icon={MessageSquare}
          accent="indigo"
        />
        <MetricCard
          label="Recommendations"
          value={formatNumber(metrics.totalRecommendations)}
          icon={ThumbsUp}
          accent="teal"
        />
        <MetricCard
          label="Share of Voice"
          value={`${metrics.shareOfVoice.toFixed(0)}%`}
          icon={PieChart}
          accent="cyan"
        />
        <MetricCard
          label="Potential Impact"
          value={formatCurrency(potentialImpact)}
          icon={TrendingUp}
          accent="blue"
          hint="Estimated opportunity — not actual revenue"
        />
      </div>

      {/* Row: category performance + gauges */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Prompt performance by category</CardTitle>
            <Badge tone="blue">Mention rate %</Badge>
          </CardHeader>
          <CardContent>
            {categoryData.length ? (
              <CategoryBarChart data={categoryData} />
            ) : (
              <p className="py-12 text-center text-sm text-text-muted">No category data.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Core rates</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            <GaugeChart value={metrics.mentionRate} label="Mention" color={CHART_COLORS.blue} />
            <GaugeChart
              value={metrics.recommendationRate}
              label="Recommend"
              color={CHART_COLORS.teal}
            />
            <GaugeChart
              value={metrics.top3InclusionRate}
              label="Top-3"
              color={CHART_COLORS.indigo}
            />
            <GaugeChart value={metrics.sentimentScore} label="Sentiment" color={CHART_COLORS.cyan} />
          </CardContent>
        </Card>
      </div>

      {/* Row: competitors + ranking distribution */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Competitor share of voice</CardTitle>
          </CardHeader>
          <CardContent>
            {competitorData.length ? (
              <HorizontalBarChart data={competitorData} color={CHART_COLORS.indigo} />
            ) : (
              <p className="py-12 text-center text-sm text-text-muted">
                No competitors were named in the answers.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Ranking distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <CategoryBarChart data={rankingData} />
          </CardContent>
        </Card>
      </div>

      {/* Top opportunities */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Top opportunities</h2>
          <span className="text-sm text-text-muted">
            {run.recommendations.length} recommendation
            {run.recommendations.length === 1 ? '' : 's'}
          </span>
        </div>
        {run.recommendations.length ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {run.recommendations.map((rec) => (
              <RecommendationCard key={rec.id} rec={rec} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No major gaps found"
            description="Your brand is performing well across the analyzed prompts."
          />
        )}
      </div>

      {/* Prompt-level table */}
      <Card>
        <CardHeader>
          <CardTitle>Prompt-level results</CardTitle>
          <span className="text-xs text-text-muted">{run.promptResults.length} prompts</span>
        </CardHeader>
        <CardContent className="px-0">
          <PromptResultsTable results={run.promptResults} />
        </CardContent>
      </Card>
    </div>
  );
}
